import React, { useState, useEffect } from "react";
import {
  Box, Container, TextField, Button, Typography, Paper, Grid,
  IconButton, Card, CardContent, InputAdornment
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PercentIcon from "@mui/icons-material/Percent";
import axios from "axios";
import toast from 'react-hot-toast'; // ✅ Import Toast

const AdminOfferManager = () => {
  const [offerData, setOfferData] = useState({
    title: "",
    description: "",
    discountPercent: "",
    bgColor: "#fff4e5", 
  });
  const [allOffers, setAllOffers] = useState([]);

  const API_URL = "http://localhost:3001/api/offers";

  const fetchOffers = async () => {
    try {
      const res = await axios.get(API_URL);
      setAllOffers(res.data);
    } catch (err) {
      console.error("Error fetching offers", err);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ✅ Loading State dikhane ke liye
    const loadingToast = toast.loading("Publishing your offer...");
    
    const finalData = { ...offerData, title: offerData.title.toUpperCase() };
    
    try {
      await axios.post(`${API_URL}/add`, finalData);
      setOfferData({ title: "", description: "", discountPercent: "", bgColor: "#fff4e5" });
      fetchOffers();
      
      // ✅ Success message with emoji
      toast.success("Offer Published & Active! 🎊", { id: loadingToast });
    } catch (err) {
      toast.error("Failed to add offer. Try again.", { id: loadingToast });
    }
  };

  const handleDelete = async (id) => {
    // Basic confirm (Aap isse bhi custom modal bana sakte hain baad mein)
    if (window.confirm("Permanent delete this offer?")) {
      try {
        await axios.delete(`${API_URL}/${id}`);
        fetchOffers();
        toast.success("Offer deleted successfully 🗑️");
      } catch (err) {
        toast.error("Error deleting offer");
      }
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8f9fa", py: 10 }}> {/* Nav ke niche space ke liye py: 10 */}
      <Container maxWidth="lg">
        <Box sx={{ mb: 6, textAlign: "left" }}>
          <Typography variant="h3" fontWeight="900" color="#3e2723" sx={{ letterSpacing: "-1px" }}>
            Offer Manager <AutoAwesomeIcon sx={{ color: "#d4a373" }} />
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage discount codes that actually work on checkout.
          </Typography>
        </Box>

        <Grid container spacing={5}>
          {/* FORM SECTION */}
          <Grid item xs={12} md={5}>
            <Box sx={{ position: "sticky", top: 100 }}> {/* Top offset set kiya hai */}
              <Paper elevation={0} sx={{ p: 4, borderRadius: 5, border: "1px solid #e0e0e0", bgcolor: "#fff" }}>
                <Typography variant="h6" fontWeight="800" mb={3} sx={{ color: "#6d3610" }}>
                  Create New Coupon
                </Typography>
                
                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Coupon Code (e.g. VISHAL20)"
                    value={offerData.title}
                    onChange={(e) => setOfferData({ ...offerData, title: e.target.value })}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LocalOfferIcon fontSize="small" /></InputAdornment>,
                    }}
                    required
                  />

                  <TextField
                    fullWidth
                    type="number"
                    label="Discount Percentage (%)"
                    placeholder="e.g. 20"
                    value={offerData.discountPercent}
                    onChange={(e) => setOfferData({ ...offerData, discountPercent: e.target.value })}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PercentIcon fontSize="small" /></InputAdornment>,
                    }}
                    required
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    value={offerData.description}
                    onChange={(e) => setOfferData({ ...offerData, description: e.target.value })}
                    sx={{ mb: 3 }}
                    required
                  />

                  <Box sx={{ p: 2, mb: 4, borderRadius: 3, bgcolor: "#f1f3f5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" fontWeight="700">Card Theme:</Typography>
                    <input
                      type="color"
                      value={offerData.bgColor}
                      onChange={(e) => setOfferData({ ...offerData, bgColor: e.target.value })}
                      style={{ width: '80px', height: '35px', cursor: 'pointer', border: '2px solid white', borderRadius: '8px' }}
                    />
                  </Box>

                  <Button 
                    fullWidth 
                    variant="contained" 
                    type="submit" 
                    size="large"
                    sx={{ bgcolor: "#6d3610", py: 1.8, borderRadius: 3, fontWeight: "800", "&:hover": { bgcolor: "#3E2723" } }}
                  >
                    PUBLISH & ACTIVATE
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Grid>

          {/* LIST SECTION */}
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h5" fontWeight="800">Active Coupons</Typography>
            </Box>

            <Grid container spacing={2}>
              {allOffers.length > 0 ? (
                allOffers.map((offer) => (
                  <Grid item xs={12} key={offer._id}>
                    <Card elevation={0} sx={{ borderRadius: 4, bgcolor: offer.bgColor, border: "1px solid rgba(0,0,0,0.05)" }}>
                      <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: "20px !important" }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Box sx={{ bgcolor: 'white', p: 1, borderRadius: 2, textAlign: 'center', minWidth: '50px' }}>
                            <Typography variant="h6" fontWeight="900" color="#6d3610">{offer.discountPercent}%</Typography>
                            <Typography variant="caption" fontWeight="bold">OFF</Typography>
                          </Box>
                          <Box>
                            <Typography variant="h6" fontWeight="900">{offer.title}</Typography>
                            <Typography variant="body2">{offer.description}</Typography>
                          </Box>
                        </Box>
                        <IconButton onClick={() => handleDelete(offer._id)} sx={{ color: "#d32f2f" }}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Typography sx={{ ml: 2, color: "text.secondary" }}>No active offers found.</Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AdminOfferManager;